import type { AuthComponent, AuthForm, AuthHandlerServiceHelpers } from '@/types/auth'

export const handleEmailCheck = async (self: AuthComponent, email: string) => {
  const response = await fetch(`/api/auth0/verify-email/${email}`)
  const result = await response.json()

  if (response.ok) {
    self.nextStep(result.length > 0 ? 'login' : 'register')
    self.user = result[0]
  } else {
    self.setMessage('Verification Error', result.error || 'Internal Server Error')
  }
}

export const handleLogin = async (
  self: AuthComponent,
  helpers: AuthHandlerServiceHelpers,
  env: Record<string, string>,
  { email, password }: Pick<AuthForm, 'email' | 'password'>,
) => {
  const response = await fetch('/api/auth0/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  })
  if (!response.redirected) {
    const result = await response.json()
    self.handleResponseError('Authentication Error', result)
    return
  }

  // If the response is redirected, it means the login was successful
  self.close()
  self.klaviyoTrack('Login', { email })
  helpers.sendCustomEvent('login', { method: 'email' })
  helpers.sendEventToStatsig('login', { method: 'email' })

  if (!self.redirectLogin && self.context === 'reviewLogin') {
    window.location.reload()
    return
  }

  if (
    !self.redirectLogin &&
    env.PUBLIC_FEATURE_SAVE === 'true' &&
    self.context === 'saveButton' &&
    self.metadata?.saveItem?.id &&
    self.metadata?.saveItem?.type
  ) {
    const userId = await helpers.getVerifiedUserId()

    if (!userId) throw new Error('User not found')

    const { id, type } = self.metadata.saveItem

    await helpers.addSave(userId, id, type)
    await helpers.syncSaves(userId)
    self.$dispatch('initSaveBtnsState')
    return
  } else {
    window.location.assign('/account/profile')
    return
  }
}

export const handleRegister = async (
  self: AuthComponent,
  helpers: AuthHandlerServiceHelpers,
  form: AuthForm,
) => {
  const { email, firstName, lastName, opt_in_core } = form

  const registerResponse = await fetch('/api/auth0/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(self.form),
  })

  const registerResult = await registerResponse.json()
  if (registerResponse.ok) {
    self.klaviyoTrack('Register', {
      email,
      'First name': firstName,
      'Last name': lastName,
      opt_in_core,
    })
    helpers.sendCustomEvent('sign_up')
    helpers.sendEventToStatsig('sign_up')

    self.nextStep('validateEmail')
  } else {
    self.handleResponseError('Registration Error', registerResult)
  }
}

export const handlePasswordReset = async (self: AuthComponent, email: string) => {
  const response = await fetch('/api/auth0/reset-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  })

  const result = await response.json()
  if (response.ok) {
    self.setMessage('Success', result)
    return
  } else {
    self.setMessage('Error', result.error || 'Internal Server Error')
    return
  }
}

export const createHandlerService = (
  alpineInstance: AuthComponent,
  helpers: AuthHandlerServiceHelpers,
  env: Record<string, string>,
) => {
  const self = alpineInstance

  return {
    checkEmail: async (email: string) => handleEmailCheck(self, email),
    login: async (email: string, password: string) =>
      handleLogin(self, helpers, env, { email, password }),
    register: async (form: AuthForm) => handleRegister(self, helpers, form),
    resetPassword: async (email: string) => handlePasswordReset(self, email),
  }
}
